import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available looks (SaaS design, 2026-09). `dark` themes also get the Tailwind
// `dark` class. `swatch` = [page, brand] for the picker.
export const THEMES = [
  { id: 'gold', label: 'Гууль', note: 'Бильярд, лаунж', dark: true, swatch: ['#121110', '#C9A45C'] },
  { id: 'cloth', label: 'Даавуу', note: 'Снукер клуб', dark: true, swatch: ['#0F1512', '#5FAE84'] },
  { id: 'night', label: 'Шөнийн цэнхэр', note: 'Караоке, тоглоомын төв', dark: true, swatch: ['#0E1116', '#7AA2E3'] },
  { id: 'burgundy', label: 'Бургунди', note: 'Лаунж, бар', dark: true, swatch: ['#140F10', '#C0616B'] },
  { id: 'coffee', label: 'Кофе', note: 'Кафе', dark: false, swatch: ['#F6F1E9', '#A8431F'] },
  { id: 'clean', label: 'Цэвэр', note: 'Ресторан, дэлгүүр', dark: false, swatch: ['#F7F7F5', '#1F5F4A'] },
  { id: 'contrast', label: 'Тод', note: 'Хурц гэрэл, жижиг дэлгэц', dark: true, swatch: ['#000000', '#FFD54F'] },
];
// Earlier theme ids keep working.
const LEGACY = { dark: 'gold', graphite: 'gold', midnight: 'night', light: 'coffee' };
const DEFAULT_DARK = 'gold';
const DEFAULT_LIGHT = 'coffee';

const byId = id => THEMES.find(theme => theme.id === id);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const initialTheme = () => {
  let saved = null;
  try { saved = localStorage.getItem('rentos-theme'); } catch { /* storage unavailable */ }
  if (LEGACY[saved]) return LEGACY[saved];
  if (byId(saved)) return saved;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return DEFAULT_DARK;
  return DEFAULT_LIGHT;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(initialTheme);

  useEffect(() => {
    try { localStorage.setItem('rentos-theme', theme); } catch { /* storage unavailable */ }
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.classList.toggle('dark', byId(theme)?.dark === true);
  }, [theme]);

  const setTheme = id => setThemeState(byId(id) ? id : (LEGACY[id] || DEFAULT_LIGHT));
  const isDark = byId(theme)?.dark === true;
  // The sun/moon button returns to the last light or dark theme used.
  const remembered = key => { try { return byId(localStorage.getItem(key)) ? localStorage.getItem(key) : null; } catch { return null; } };
  useEffect(() => {
    try { localStorage.setItem(isDark ? 'rentos-theme-dark' : 'rentos-theme-light', theme); } catch { /* storage unavailable */ }
  }, [theme, isDark]);
  const toggleTheme = () => setThemeState(isDark ? (remembered('rentos-theme-light') || DEFAULT_LIGHT) : (remembered('rentos-theme-dark') || DEFAULT_DARK));

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
